import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useWatch } from 'react-hook-form';
import Styles from '@/assets/js/Styles';
import {
  FormControl,
  Box,
  Typography,
  Tooltip,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { DMArtifact, DMMediaModel } from '@/declarations/models/DMMediaModel';
import { GenericMedia } from '@/declarations/GenericMedia';
import TextInput from '@/components/forms/TextInput';
import CheckboxInput from '@/components/forms/CheckboxInput';
import SwitchInput from '@/components/forms/SwitchInput';
import { BannerBlock } from '@/declarations/models/blocks/BannerBlock';
import { MediaResourceType } from '@/declarations/models/MediaResourceType';
import { FinderButton, FinderType } from '@/components/Finder/FinderButton';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { VideoBlock as VideoBlockModel } from '@/declarations/models/blocks/VideoBlock';
import Image from '@/components/Image';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import Container from '../../../../components/Container';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import { Api } from '../../../../services/Api';
import SingleDMItem from '../DMBlocks/SingleDMItem';

const SelectedRow: FC<{ icon: React.ReactNode; children: React.ReactNode; onRemove?: () => void }> = ({
  icon,
  children,
  onRemove,
}) => {
  const { t: tCommon } = useTranslation('common');
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        minHeight: '50px',
      }}>
      <Box
        sx={{
          borderBottomLeftRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
          borderTopLeftRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
          backgroundColor: Styles.Colors.MEDIUM_DARK_GREY,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100px',
        }}>
        {icon}
      </Box>
      <Box
        sx={{
          display: 'flex',
          p: 0,
          backgroundColor: '#fff',
          flex: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottomRightRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
          borderTopRightRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
        }}>
        {children}

        {!!onRemove && (
          <Tooltip title={tCommon('remove')}>
            <IconButton onClick={onRemove}>
              <MaterialSymbol name='do_not_disturb_on' />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export const VideoBlock: BlockComponent = () => {
  const { t: tCommon } = useTranslation('common');
  const { t: tComponents } = useTranslation('components');
  const { blockPath } = useCurrentBlock();
  const [dmArtifact, setDmArtifact] = useState<DMArtifact>();

  const [showLocalAudioFields, setShowLocalAudioFields] = useState(false);

  const mediaObject = useWatch<BlockSpecificPage<BannerBlock>, `${BlockPath}.mediaobject`>({
    name: `${blockPath}.mediaobject`,
  });

  const {
    field: { value: video, onChange: onChangeVideo },
  } = useController<BlockSpecificPage<VideoBlockModel>, `${BlockPath}.video`>({
    name: `${blockPath}.video`,
  });

  const {
    field: { value: source, onChange: onChangeSource },
  } = useController<BlockSpecificPage<VideoBlockModel>, `${BlockPath}.source`>({
    name: `${blockPath}.source`,
  });

  const {
    field: { value: provider, onChange: onChangeProvider },
  } = useController<BlockSpecificPage<VideoBlockModel>, `${BlockPath}.provider`>({
    name: `${blockPath}.provider`,
  });

  const {
    field: { value: dmItem, onChange: onChangeDmItem },
  } = useController<BlockSpecificPage<VideoBlockModel>, `${BlockPath}.dmItem`>({
    name: `${blockPath}.dmItem`,
  });

  const {
    field: { value: iframeSrc, onChange: onChangeIframeSrc },
  } = useController<BlockSpecificPage<VideoBlockModel>, `${BlockPath}.iframeSrc`>({
    name: `${blockPath}.iframeSrc`,
  });

  const {
    field: { value: convertedFromWidget, onChange: onChangeConvertedFromWidget },
  } = useController<BlockSpecificPage<VideoBlockModel>, `${BlockPath}.convertedFromWidget`>({
    name: `${blockPath}.convertedFromWidget`,
  });

  function handleChangeDMobject(item: DMMediaModel | null): void {
    if (item) {
      // console.log('DM Audio selected:', item);
      // set the dmItem with all required DMArtifact properties
      onChangeDmItem({
        uniqueId: item['artifact.uniqueId'],
        title: item['artifact.ingress.title'],
        // add any other required fields from DMArtifact if necessary
      });
      onChangeVideo(null);
    }
  }

  const handleFocusPointChanged = (focusPoint: { x: string; y: string }) => {
    onChangeVideo({
      ...video,
      local: {
        ...video?.local,
        x: focusPoint.x,
        y: focusPoint.y,
      },
    });
  };

  useEffect(() => {
    if (dmItem) {
      if (dmItem.uniqueId) {
        const ctx = Api.getDMArtifact(dmItem.uniqueId);

        ctx
          .fetchDirect(null)
          .then((res) => !!res && setDmArtifact(res))
          .finally(ctx.abort);
      }
    } else setDmArtifact(undefined);
  }, [dmItem]);
  return (
    <Container gap={2} mt={-2} column top left fullWidth>
      <Container gap={2} column mt={2} top left fullWidth>
        <Container gap={1} fullWidth left>
          <FormControl>
            <h3 className='block-sub-label'> {tComponents('VideoBlock.source')}:</h3>
            <RadioGroup row value={source} onChange={(e) => onChangeSource(e.target.value)}>
              <FormControlLabel value='ekultur' control={<Radio />} label={tComponents('AudioBlock.sourceEkultur')} />
              <FormControlLabel value='external' control={<Radio />} label={tComponents('VideoBlock.sourceExternal')} />
            </RadioGroup>
          </FormControl>
        </Container>

        {source === 'ekultur' && (
          <Container column gap={1} mt={2} fullWidth>
            <Container fullWidth mb={2}>
              <FinderButton
                type={FinderType.M24}
                overrideText={tComponents('VideoBlock.pickM24Video')}
                overrideIcon={<MaterialSymbol name='movie' />}
                multiSelect={false}
                finderProps={{
                  selectableResourceTypes: [MediaResourceType.VIDEO],
                  onSelectionConfirmed: (selected) => {
                    onChangeVideo(selected[0].source);
                    onChangeDmItem(null);
                  },
                  defaultResourceType: MediaResourceType.VIDEO,
                }}
              />

              <FinderButton
                type={FinderType.DM}
                overrideText={tComponents('VideoBlock.pickDMVideo')}
                finderProps={{
                  onSelectionConfirmed: (items: Array<GenericMedia<DMMediaModel>>) =>
                    items.forEach((item) => handleChangeDMobject(item.source)),
                  hasVideoDep: true,
                }}
                multiSelect={false}
              />
            </Container>

            {!!video && (
              <Container gap={2} column fullWidth sx={{ p: 2, background: '#efefef' }}>
                <SelectedRow
                  icon={<MaterialSymbol name='movie' sx={{ fontSize: 48, color: 'white' }} />}
                  onRemove={() => {
                    onChangeVideo(null);
                  }}>
                  <Container sx={{ width: '100%' }} top>
                    {video?.content?.poster && (
                      <>
                        <Image
                          src={`${video.content?.poster?.url || mediaObject?.url || ''}`}
                          title={video.content?.poster?.title}
                          alt={video.content?.poster?.content?.alttext || mediaObject?.local?.caption}
                          sx={{ maxWidth: '200px' }}
                        />
                      </>
                    )}
                    <Container sx={{ width: '100%' }} p={2} column left>
                      <Typography sx={{ fontWeight: 400 }}>
                        <strong>{tComponents('AudioBlock.title')}:</strong>{' '}
                        {video.title || video.content?.title || 'N/A'}
                        <br />
                        <strong>{tComponents('AudioBlock.file')}:</strong> {video.filename || 'N/A'}
                      </Typography>
                      <Typography variant='caption' color='textSecondary'>
                        {video.content?.caption}
                      </Typography>
                    </Container>
                  </Container>
                </SelectedRow>

                <Container gap={2} column fullWidth mt={2} left>
                  <TextInput<BlockSpecificPage<VideoBlockModel>>
                    path={`${blockPath}.video.local.caption`}
                    label={tComponents('VideoBlock.localCaption')}
                    multiline
                    minRows={2}
                    maxRows={4}
                    fullWidth
                  />
                  <SwitchInput<BlockSpecificPage<VideoBlockModel>>
                    path={`${blockPath}.video.local.disableCredits`}
                    label={tComponents('VideoBlock.disableCredits')}
                    fullWidth={false}
                  />
                </Container>
              </Container>
            )}

            {!!dmArtifact && (
              <Container gap={2} column fullWidth sx={{ p: 2, background: '#efefef' }}>
                <SingleDMItem dmArtifact={dmArtifact} onRemove={() => onChangeDmItem(null)} />
              </Container>
            )}
          </Container>
        )}

        {source === 'external' && (
          <Container gap={2} column fullWidth mt={2}>
            <FormControl fullWidth>
              <InputLabel>{tComponents('VideoBlock.provider')}</InputLabel>
              <Select sx={{ width: '250px' }} value={provider ?? ''} onChange={(e) => onChangeProvider(e.target.value)}>
                <MenuItem value='YouTube.com'>YouTube</MenuItem>
                <MenuItem value='Vimeo.com'>Vimeo</MenuItem>
                <MenuItem value='NRK.no'>NRK</MenuItem>
                <MenuItem value='Facebook.com'>Facebook</MenuItem>
                <MenuItem value='ur.se'>UR (Sveriges utbildningsradio)</MenuItem>
                <MenuItem value='Other'>{tComponents('VideoBlock.other')}</MenuItem>
              </Select>
              <FormHelperText>{tComponents('VideoBlock.providerHelpText')}</FormHelperText>
            </FormControl>

            <Container gap={2} column top left fullWidth>
              <Container gap={2} top left fullWidth>
                <TextInput<BlockSpecificPage<VideoBlockModel>>
                  path={`${blockPath}.html`}
                  label={tComponents('WidgetBlock.html')}
                  type='text'
                  multiline
                  minRows={5}
                  maxRows={10}
                />
              </Container>
              <Container gap={2} top left fullWidth>
                <CheckboxInput<BlockSpecificPage<VideoBlockModel>>
                  path={`${blockPath}.isWidget`}
                  label={tComponents('WidgetBlock.isWidget')}
                />
              </Container>

              <Container
                column
                fullWidth
                px={2}
                py={1}
                mt={0}
                gap={0}
                left
                sx={{
                  backgroundColor: Styles.Colors.LIGHT_BLUE,
                  border: `1px solid ${Styles.Colors.MEDIUM_BLUE}`,
                }}>
                <p dangerouslySetInnerHTML={{ __html: tComponents('AudioBlock.securityInfo') }} />
              </Container>
            </Container>

            <Container gap={1} column top left fullWidth>
              <h3 className='block-sub-label'>{tComponents('WidgetBlock.iframeSettings')}</h3>

              <Container gap={2} top left fullWidth>
                <TextInput<BlockSpecificPage<VideoBlockModel>>
                  path={`${blockPath}.iframeSrc`}
                  label={tComponents('WidgetBlock.src')}
                />
                <Box sx={{ width: '30%' }}>
                  <TextInput<BlockSpecificPage<VideoBlockModel>>
                    path={`${blockPath}.iframeHeight`}
                    label={tComponents('WidgetBlock.height')}
                  />
                </Box>
              </Container>
            </Container>

            <Container gap={1} column top left fullWidth>
              <h3 className='block-sub-label'>{tComponents('WidgetBlock.additionalSettings')}</h3>
              <TextInput<BlockSpecificPage<VideoBlockModel>>
                path={`${blockPath}.externalUrl`}
                label={tComponents('WidgetBlock.externalUrl')}
              />
            </Container>
          </Container>
        )}
      </Container>
    </Container>
  );
};

export default VideoBlock;
